import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = {
  // Clients
  getClients: () => axios.get(`${API_URL}/clients`),
  getClientConsumablesBudget: (clientId) => axios.get(`${API_URL}/clients/${clientId}/consumables-budget`),
  getClient: (id) => axios.get(`${API_URL}/clients/${id}`),
  createClient: (data) => axios.post(`${API_URL}/clients`, data),
  updateClient: (id, data) => axios.put(`${API_URL}/clients/${id}`, data),
  deleteClient: (id) => axios.delete(`${API_URL}/clients/${id}`),
  // Sites
  getSites: () => axios.get(`${API_URL}/sites`),
  exportSitesCSV: () => axios.get(`${API_URL}/sites/export-csv`, { responseType: 'blob' }),
  getSite: (id) => axios.get(`${API_URL}/sites/${id}`),
  createSite: (data) => axios.post(`${API_URL}/sites`, data),
  updateSite: (id, data) => axios.put(`${API_URL}/sites/${id}`, data),
  deleteSite: (id) => axios.delete(`${API_URL}/sites/${id}`),
  setFinishDate: (siteId, finishDate) => axios.post(`${API_URL}/sites/${siteId}/set-finish-date`, null, { params: { finish_date: finishDate } }),
  confirmJobRemovals: (siteId) => axios.post(`${API_URL}/sites/${siteId}/confirm-job-removals`),
  getSitePricing: (siteId) => axios.get(`${API_URL}/sites/${siteId}/pricing`),
  getSiteAllowedStock: (siteId) => axios.get(`${API_URL}/sites/${siteId}/allowed-stock`),
  importStockToSite: (siteId) => axios.post(`${API_URL}/sites/${siteId}/import-stock`),
  updateSitePricing: (siteId, stockItemId, price) => 
    axios.put(`${API_URL}/sites/${siteId}/pricing/${stockItemId}`, { stock_item_id: stockItemId, special_price: price }),
  deleteSitePricing: (siteId, stockItemId) => axios.delete(`${API_URL}/sites/${siteId}/pricing/${stockItemId}`),

  // Site Services
  getSiteServices: (siteId) => axios.get(`${API_URL}/sites/${siteId}/services`),
  createSiteService: (siteId, data) => axios.post(`${API_URL}/sites/${siteId}/services`, data),
  updateSiteService: (siteId, serviceId, data) => axios.put(`${API_URL}/sites/${siteId}/services/${serviceId}`, data),
  deleteSiteService: (siteId, serviceId) => axios.delete(`${API_URL}/sites/${siteId}/services/${serviceId}`),
  getSitePOReferences: (siteId) => axios.get(`${API_URL}/sites/${siteId}/po-references`),
  getServiceCodeSuggestions: () => axios.get(`${API_URL}/sites/service-codes/suggestions`),

  // Service Code Templates
  getServiceCodeTemplates: () => axios.get(`${API_URL}/service-code-templates`),
  getServiceCodeTemplate: (code) => axios.get(`${API_URL}/service-code-templates/${code}`),
  createServiceCodeTemplate: (data) => axios.post(`${API_URL}/service-code-templates`, data),
  updateServiceCodeTemplate: (id, data) => axios.put(`${API_URL}/service-code-templates/${id}`, data),
  deleteServiceCodeTemplate: (id) => axios.delete(`${API_URL}/service-code-templates/${id}`),

  // Company Settings
  getCompanySettings: () => axios.get(`${API_URL}/settings/company`),
  updateCompanySettings: (data) => axios.put(`${API_URL}/settings/company`, data),

  // Delivery Notes
  getDeliveryNote: (orderId) => `${API_URL}/orders/${orderId}/delivery-note`,

  // Print Invoice
  getPrintInvoice: (invoiceId) => `${API_URL}/invoices/${invoiceId}/print`,

  // Client Pricing
  getClientPricing: (clientId) => axios.get(`${API_URL}/clients/${clientId}/pricing`),
  importStockToClient: (clientId) => axios.post(`${API_URL}/clients/${clientId}/import-stock`),
  updateClientPricing: (clientId, stockItemId, price) => axios.put(`${API_URL}/clients/${clientId}/pricing/${stockItemId}`, { special_price: price }),
  deleteClientPricing: (clientId, stockItemId) => axios.delete(`${API_URL}/clients/${clientId}/pricing/${stockItemId}`),
  
  // Client allowed stock management
  getClientAllowedStock: (clientId) => axios.get(`${API_URL}/clients/${clientId}/allowed-stock`),
  addStockToClient: (clientId, stockItemId, specialPrice = null) => 
    axios.post(`${API_URL}/clients/${clientId}/allowed-stock/${stockItemId}${specialPrice ? `?special_price=${specialPrice}` : ''}`),
  removeStockFromClient: (clientId, stockItemId) => axios.delete(`${API_URL}/clients/${clientId}/allowed-stock/${stockItemId}`),
  toggleClientStockItem: (clientId, stockItemId) => axios.put(`${API_URL}/clients/${clientId}/allowed-stock/${stockItemId}/toggle`),
  syncPricingToAllowed: (clientId) => axios.post(`${API_URL}/clients/${clientId}/sync-pricing-to-allowed`),

  // Contract Intelligence
  getClientContract: (clientId) => axios.get(`${API_URL}/clients/${clientId}/contract`),
  createClientContract: (clientId) => axios.post(`${API_URL}/clients/${clientId}/contract`),
  uploadContractDocument: (clientId, formData) => axios.post(`${API_URL}/clients/${clientId}/contract/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,  // 5 min for large PDFs
  }),
  deleteContractDocument: (clientId, documentId) => axios.delete(`${API_URL}/clients/${clientId}/contract/documents/${documentId}`),
  analyzeContract: (clientId) => axios.post(`${API_URL}/clients/${clientId}/contract/analyze`),
  updateContractSummary: (clientId, summary) => axios.put(`${API_URL}/clients/${clientId}/contract/summary`, summary),
  confirmContractExtraction: (clientId) => axios.post(`${API_URL}/clients/${clientId}/contract/confirm`),
  addPeriodicTask: (clientId, task) => axios.post(`${API_URL}/clients/${clientId}/contract/periodic-tasks`, task),
  applyWorkingWeeks: (clientId) => axios.post(`${API_URL}/clients/${clientId}/contract/apply-working-weeks`),
  completePeriodicTask: (clientId, taskId) => axios.post(`${API_URL}/clients/${clientId}/contract/periodic-tasks/${taskId}/complete`),
  getDuePeriodicTasks: (days = 60) => axios.get(`${API_URL}/periodic-tasks/due?days=${days}`),
  updatePeriodicTask: (clientId, taskId, task) => axios.put(`${API_URL}/clients/${clientId}/contract/periodic-tasks/${taskId}`, task),
  deletePeriodicTask: (clientId, taskId) => axios.delete(`${API_URL}/clients/${clientId}/contract/periodic-tasks/${taskId}`),
  getPeriodicTaskTypes: () => axios.get(`${API_URL}/periodic-tasks/types`),
  getPeriodicTasksDashboard: (month, year) => axios.get(`${API_URL}/periodic-tasks/dashboard`, { params: { month, year } }),

  // Site Pricing
  getSitePricing: (siteId) => axios.get(`${API_URL}/sites/${siteId}/pricing`),
  importStockToSite: (siteId) => axios.post(`${API_URL}/sites/${siteId}/import-stock`),
  updateSitePricing: (siteId, stockItemId, data) => axios.put(`${API_URL}/sites/${siteId}/pricing/${stockItemId}`, data),
  deleteSitePricing: (siteId, stockItemId) => axios.delete(`${API_URL}/sites/${siteId}/pricing/${stockItemId}`),

  // Stock
  getStock: () => axios.get(`${API_URL}/stock`),
  getChargeableStock: () => axios.get(`${API_URL}/stock/chargeable`),
  getNonChargeableStock: () => axios.get(`${API_URL}/stock/non-chargeable`),
  createStockItem: (data) => axios.post(`${API_URL}/stock`, data),
  updateStockItem: (id, data) => axios.put(`${API_URL}/stock/${id}`, data),
  deleteStockItem: (id) => axios.delete(`${API_URL}/stock/${id}`),
  processStockItemBackorders: (stockItemId) => axios.post(`${API_URL}/stock-items/${stockItemId}/process-backorders`),

  // Stock Usage Reports
  getStockUsageReport: (params) => axios.post(`${API_URL}/reports/stock-usage`, params),
  getStockItemsForReport: (search) => axios.get(`${API_URL}/reports/stock-items-lookup`, { params: { search } }),
  getClientsForReport: () => axios.get(`${API_URL}/reports/clients-lookup`),
  getSitesForReport: (clientCode) => axios.get(`${API_URL}/reports/sites-lookup`, { params: { client_code: clientCode } }),

  // Backorders
  getBackorders: () => axios.get(`${API_URL}/backorders`),
  processAllBackorders: () => axios.post(`${API_URL}/backorders/process-all`),
  getBackorderDeliveryNote: (orderId) => `${API_URL}/orders/${orderId}/backorder-delivery-note`,
  generateBackorderDN: (orderId) => axios.post(`${API_URL}/orders/${orderId}/generate-backorder-dn`),

  // Purchase Orders
  getPurchaseOrders: () => axios.get(`${API_URL}/purchase-orders`),
  createPurchaseOrder: (data) => axios.post(`${API_URL}/purchase-orders`, data),
  receivePurchaseOrder: (id) => axios.put(`${API_URL}/purchase-orders/${id}/receive`),
  receivePurchaseOrderPartial: (id, items) => axios.put(`${API_URL}/purchase-orders/${id}/receive-partial`, { items }),

  // Orders
  getOrders: (params) => axios.get(`${API_URL}/orders`, { params }),
  getPendingChargeableOrders: () => axios.get(`${API_URL}/orders/pending-chargeable`),
  getSiteOrderHistory: (siteId, limit = 20) => axios.get(`${API_URL}/sites/${siteId}/order-history?limit=${limit}`),
  createOrder: (data) => axios.post(`${API_URL}/orders`, data),
  cancelOrder: (id, reason) => axios.post(`${API_URL}/orders/${id}/cancel`, { reason }),
  postChargeableOrders: () => axios.post(`${API_URL}/orders/post-chargeable`),
  
  // Returns/Collections
  getReturnRequests: () => axios.get(`${API_URL}/orders/returns`),
  createReturnRequest: (data) => axios.post(`${API_URL}/orders/returns`, data),
  approveReturn: (returnId) => axios.put(`${API_URL}/orders/returns/${returnId}/approve`),
  rejectReturn: (returnId, reason) => axios.put(`${API_URL}/orders/returns/${returnId}/reject?reason=${encodeURIComponent(reason)}`),
  processCollection: (returnId) => axios.put(`${API_URL}/orders/returns/${returnId}/collect`),

  // Invoices
  getInvoices: () => axios.get(`${API_URL}/invoices`),
  createInvoice: (data) => axios.post(`${API_URL}/invoices`, data),
  markInvoicePaid: (id) => axios.put(`${API_URL}/invoices/${id}/mark-paid`),
  generateMonthlyInvoices: () => axios.post(`${API_URL}/invoices/generate-monthly`),
  
  // Client/Site Invoice Filtering & Statements
  getClientInvoices: (clientId, status = null) => {
    const params = status && status !== 'all' ? `?status=${status}` : '';
    return axios.get(`${API_URL}/clients/${clientId}/invoices${params}`);
  },
  getSiteInvoices: (siteId, status = null) => {
    const params = status && status !== 'all' ? `?status=${status}` : '';
    return axios.get(`${API_URL}/sites/${siteId}/invoices${params}`);
  },
  getClientStatementUrl: (clientId) => `${API_URL}/clients/${clientId}/statement`,
  emailClientStatement: (clientId) => axios.post(`${API_URL}/clients/${clientId}/statement/email`),
  
  // Invoice Cycles
  getInvoiceCycles: (showPast = false) => axios.get(`${API_URL}/invoice-cycles?show_past=${showPast}`),
  generateInvoiceCycles: () => axios.post(`${API_URL}/invoice-cycles/generate`),
  updateInvoiceCycleStatus: (cycleId, status) => axios.put(`${API_URL}/invoice-cycles/${cycleId}/status?status=${status}`),
  postRegularInvoices: (cycleId) => axios.post(`${API_URL}/invoice-cycles/${cycleId}/post-regular-invoices`),
  postDraftInvoices: (cycleId, autoEmail = false) => axios.post(`${API_URL}/invoice-cycles/${cycleId}/post-draft-invoices?auto_email=${autoEmail}`),
  getCycleInvoices: (cycleId) => axios.get(`${API_URL}/invoice-cycles/${cycleId}/invoices`),
  downloadInvoicePdf: (invoiceId) => `${API_URL}/invoices/${invoiceId}/pdf`,
  sendInvoiceEmail: (invoiceId, recipientEmail = null) => {
    const params = recipientEmail ? `?recipient_email=${encodeURIComponent(recipientEmail)}` : '';
    return axios.post(`${API_URL}/invoices/${invoiceId}/send-email${params}`);
  },
  sendAllCycleInvoiceEmails: (cycleId) => axios.post(`${API_URL}/invoice-cycles/${cycleId}/send-all-emails`),
  
  // Quotes
  getQuotes: () => axios.get(`${API_URL}/quotes`),
  getQuote: (quoteId) => axios.get(`${API_URL}/quotes/${quoteId}`),
  createQuote: (data) => axios.post(`${API_URL}/quotes`, data),
  updateQuoteStatus: (quoteId, status) => axios.put(`${API_URL}/quotes/${quoteId}/status?status=${status}`),
  deleteQuote: (quoteId) => axios.delete(`${API_URL}/quotes/${quoteId}`),
  convertQuoteToInvoice: (quoteId, data) => axios.post(`${API_URL}/quotes/${quoteId}/convert-to-invoice`, null, { params: data }),
  checkStaffAvailability: (quoteId, data) => axios.post(`${API_URL}/quotes/${quoteId}/check-staff-availability`, data),
  sendQuoteEmail: (quoteId) => axios.post(`${API_URL}/quotes/${quoteId}/send-email`),
  approveQuoteInternal: (quoteId) => axios.post(`${API_URL}/quotes/${quoteId}/approve`),
  rejectQuoteInternal: (quoteId) => axios.post(`${API_URL}/quotes/${quoteId}/reject`),
  createQuoteRevision: (quoteId) => axios.post(`${API_URL}/quotes/${quoteId}/create-revision`),
  getQuoteRevisions: (quoteId) => axios.get(`${API_URL}/quotes/${quoteId}/revisions`),
  
  // Service Code Templates  
  getServiceCodeTemplates: () => axios.get(`${API_URL}/service-code-templates`),

  // Credit Notes
  getCreditNotes: () => axios.get(`${API_URL}/credit-notes`),
  getAreaManagers: () => axios.get(`${API_URL}/area-managers`),
  getAreaManagerReportUrl: (staffId) => `${API_URL}/reports/area-manager/${staffId}/print`,
  getCreditCodeSuggestions: (q, siteId) => axios.get(`${API_URL}/credit-code-suggestions`, { params: { q, ...(siteId ? { site_id: siteId } : {}) } }),
  getCreditNote: (id) => axios.get(`${API_URL}/credit-notes/${id}`),
  createCreditNote: (data) => axios.post(`${API_URL}/credit-notes`, data),
  updateCreditNote: (id, data) => axios.put(`${API_URL}/credit-notes/${id}`, data),
  postCreditNote: (id) => axios.post(`${API_URL}/credit-notes/${id}/post`),
  deleteCreditNote: (id) => axios.delete(`${API_URL}/credit-notes/${id}`),
  getPrintCreditNote: (creditNoteId) => `${API_URL}/credit-notes/${creditNoteId}/print`,
  emailCreditNote: (creditNoteId, customMessage = null) => axios.post(`${API_URL}/credit-notes/${creditNoteId}/email`, null, {
    params: customMessage ? { custom_message: customMessage } : {}
  }),
  getClientBalance: (clientId) => axios.get(`${API_URL}/clients/${clientId}/balance`),

  // Invoice PO Update and Resend
  updateInvoicePO: (invoiceId, poReference) => axios.put(`${API_URL}/invoices/${invoiceId}/po-reference`, { po_reference: poReference }),
  resendInvoice: (invoiceId, customMessage = null) => axios.post(`${API_URL}/invoices/${invoiceId}/resend`, null, {
    params: customMessage ? { custom_message: customMessage } : {}
  }),

  // Suppliers
  getSuppliers: () => axios.get(`${API_URL}/suppliers`),
  getSupplier: (id) => axios.get(`${API_URL}/suppliers/${id}`),
  createSupplier: (data) => axios.post(`${API_URL}/suppliers`, data),
  updateSupplier: (id, data) => axios.put(`${API_URL}/suppliers/${id}`, data),
  deleteSupplier: (id) => axios.delete(`${API_URL}/suppliers/${id}`),
  getSupplierStockItems: (supplierId) => axios.get(`${API_URL}/suppliers/${supplierId}/stock-items`),

  // Stock-Supplier Relationships
  getStockSuppliers: (stockItemId) => axios.get(`${API_URL}/stock/${stockItemId}/suppliers`),
  addStockSupplier: (stockItemId, data) => axios.post(`${API_URL}/stock/${stockItemId}/suppliers`, data),
  updateStockSupplier: (stockItemId, supplierId, data) => axios.put(`${API_URL}/stock/${stockItemId}/suppliers/${supplierId}`, data),
  deleteStockSupplier: (stockItemId, supplierId) => axios.delete(`${API_URL}/stock/${stockItemId}/suppliers/${supplierId}`),

  // Email Configuration
  getEmailConfigs: () => axios.get(`${API_URL}/email-configs`),
  getEmailConfig: (configType) => axios.get(`${API_URL}/email-configs/${configType}`),
  createEmailConfig: (data) => axios.post(`${API_URL}/email-configs`, data),
  testEmailConfig: (configType, testRecipient) => axios.post(`${API_URL}/email-configs/test?config_type=${configType}&test_recipient=${testRecipient}`),
  getEmailLogs: (limit) => axios.get(`${API_URL}/email-logs?limit=${limit || 100}`),

  // Supplier Purchase Orders
  generateSupplierPO: (orderId) => axios.post(`${API_URL}/orders/${orderId}/generate-po`),
  getOrderPO: (orderId) => axios.get(`${API_URL}/orders/${orderId}/po`),
  sendPOToSupplier: (poId) => axios.post(`${API_URL}/supplier-purchase-orders/${poId}/send`),
  getSupplierPOs: () => axios.get(`${API_URL}/supplier-purchase-orders`),
  
  // Backorder Fulfillment
  fulfillBackorder: (orderId, itemsToFulfill) => axios.post(`${API_URL}/orders/${orderId}/fulfill-backorder`, itemsToFulfill),
  
  // Order Editing
  checkOrderEditable: (orderId) => axios.get(`${API_URL}/orders/${orderId}/can-edit`),
  editOrderQuantities: (orderId, updatedItems, itemsToRemove = null, itemsToAdd = null, notes = null, siteChange = null, poReference = null) => 
    axios.put(`${API_URL}/orders/${orderId}/edit-quantities`, {
      updated_items: updatedItems,
      items_to_remove: itemsToRemove,
      items_to_add: itemsToAdd,
      notes: notes,
      site_change: siteChange,
      po_reference: poReference
    }),
  getSiteStockItems: (siteId) => axios.get(`${API_URL}/sites/${siteId}/allowed-stock`),
  
  // Route Assignment
  assignDriverVehicle: (routeId, assignment) => axios.put(`${API_URL}/routes/${routeId}/assign`, assignment),
  getDrivers: () => axios.get(`${API_URL}/staff?is_driver=true`),

  // Stock Replenishment
  getReplenishmentAnalysis: () => axios.get(`${API_URL}/stock/replenishment-analysis`),
  bulkCreatePurchaseOrders: (items) => axios.post(`${API_URL}/purchase-orders/bulk-create`, items),

  // Stock Usage Analysis (Phase B)
  getUsageAnalysis: () => axios.get(`${API_URL}/stock/usage-analysis`),
  updateAllReorderLevels: (forceUpdate = false) => axios.post(`${API_URL}/stock/update-reorder-levels?force_update=${forceUpdate}`),
  updateSingleReorderLevel: (itemId, newLevel) => axios.post(`${API_URL}/stock/${itemId}/update-reorder-level?new_level=${newLevel}`),

  // Fleet Management
  getFleetVehicles: () => axios.get(`${API_URL}/fleet/vehicles`),
  getVehicle: (vehicleId) => axios.get(`${API_URL}/fleet/vehicles/${vehicleId}`),
  createVehicle: (vehicleData) => axios.post(`${API_URL}/fleet/vehicles`, vehicleData),
  updateVehicle: (vehicleId, vehicleData) => axios.put(`${API_URL}/fleet/vehicles/${vehicleId}`, vehicleData),
  deleteVehicle: (vehicleId) => axios.delete(`${API_URL}/fleet/vehicles/${vehicleId}`),
  getVehicleServices: (vehicleId) => axios.get(`${API_URL}/fleet/vehicles/${vehicleId}/services`),
  addServiceRecord: (vehicleId, serviceData) => axios.post(`${API_URL}/fleet/vehicles/${vehicleId}/services`, serviceData),
  deleteServiceRecord: (serviceId) => axios.delete(`${API_URL}/fleet/services/${serviceId}`),
  syncVehicleWithDVLA: (vehicleId) => axios.post(`${API_URL}/fleet/vehicles/${vehicleId}/sync-dvla`),
  syncAllVehiclesWithDVLA: () => axios.post(`${API_URL}/fleet/vehicles/sync-all-dvla`),
  lookupVehicleFromDVLA: (registration) => axios.post(`${API_URL}/fleet/lookup-dvla?registration=${registration}`),
  testDVLAConnection: () => axios.post(`${API_URL}/fleet/test-dvla-connection`),
  getFleetComplianceStatus: () => axios.get(`${API_URL}/fleet/compliance-status`),

  // Routes & Delivery Management
  getRoutes: (dateFrom, dateTo) => axios.get(`${API_URL}/routes`, { params: { date_from: dateFrom, date_to: dateTo } }),
  getDispatchedOrdersRoutes: () => axios.get(`${API_URL}/routes/dispatched-orders`),
  getRoute: (routeId) => axios.get(`${API_URL}/routes/${routeId}`),
  createRoute: (routeData) => axios.post(`${API_URL}/routes`, routeData),
  updateRoute: (routeId, routeData) => axios.put(`${API_URL}/routes/${routeId}`, routeData),
  deleteRoute: (routeId) => axios.delete(`${API_URL}/routes/${routeId}`),
  addOrdersToRoute: (routeId, orderIds) => axios.post(`${API_URL}/routes/${routeId}/add-orders`, orderIds),
  removeOrderFromRoute: (routeId, orderId) => axios.delete(`${API_URL}/routes/${routeId}/orders/${orderId}`),
  regenerateRoutePin: (routeId) => axios.post(`${API_URL}/routes/${routeId}/regenerate-pin`),
  dispatchRoute: (routeId) => axios.post(`${API_URL}/routes/${routeId}/dispatch`),
  // Driver mobile access
  getRouteByPin: (pinCode) => axios.get(`${API_URL}/routes/by-pin/${pinCode}`),
  updateDeliveryStatus: (pinCode, orderId, status, podData) => 
    axios.post(`${API_URL}/routes/by-pin/${pinCode}/update-delivery?order_id=${orderId}&status=${status}`, podData),

  // Staff Management
  getStaff: (search) => axios.get(`${API_URL}/staff${search ? `?search=${search}` : ''}`),
  getStaffById: (staffId) => axios.get(`${API_URL}/staff/${staffId}`),
  createStaff: (staffData) => axios.post(`${API_URL}/staff`, staffData),
  updateStaff: (staffId, staffData) => axios.put(`${API_URL}/staff/${staffId}`, staffData),
  updateStaffJob: (staffId, jobId, jobData) => axios.put(`${API_URL}/staff/${staffId}/jobs/${jobId}`, jobData),
  deleteStaff: (staffId) => axios.delete(`${API_URL}/staff/${staffId}`),
  uploadStaffPhoto: (staffId, formData) => axios.post(`${API_URL}/staff/upload-photo/${staffId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getStaffPhoto: (filename) => `${API_URL}/staff/photo/${filename}`,
  generateIdBadge: (staffId) => `${API_URL}/staff/${staffId}/generate-badge`,
  importStaffCSV: (formData) => axios.post(`${API_URL}/staff/import-csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  createUserForStaff: (staffId, userData) => axios.post(`${API_URL}/staff/${staffId}/create-user`, userData),
  setStaffAsLeaver: (staffId, leaverData) => axios.post(`${API_URL}/staff/${staffId}/set-leaver`, leaverData),
  
  // Staff Jobs (with history)
  getStaffJobs: (staffId, includeEnded = false) => axios.get(`${API_URL}/staff/${staffId}/jobs?include_ended=${includeEnded}`),
  endStaffJob: (staffId, jobId, data) => axios.patch(`${API_URL}/staff/${staffId}/jobs/${jobId}/end`, data),
  
  // Sick Pay Alerts
  getSickPayAlerts: (status = null, severity = null) => {
    let url = `${API_URL}/sick-pay-alerts`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (severity) params.push(`severity=${severity}`);
    if (params.length > 0) url += `?${params.join('&')}`;
    return axios.get(url);
  },
  getSickPayAlert: (alertId) => axios.get(`${API_URL}/sick-pay-alerts/${alertId}`),
  getStaffSickPayAlerts: (staffId, status = null) => {
    let url = `${API_URL}/staff/${staffId}/sick-pay-alerts`;
    if (status) url += `?status=${status}`;
    return axios.get(url);
  },
  acknowledgeSickPayAlert: (alertId) => axios.patch(`${API_URL}/sick-pay-alerts/${alertId}/acknowledge`),
  dismissSickPayAlert: (alertId) => axios.patch(`${API_URL}/sick-pay-alerts/${alertId}/dismiss`),
  deleteSickPayAlert: (alertId) => axios.delete(`${API_URL}/sick-pay-alerts/${alertId}`),
  
  // Staff Payslips
  getStaffPayslips: (staffId, months = null) => axios.get(`${API_URL}/staff/${staffId}/payslips${months ? `?months=${months}` : ''}`),
  batchResendPayslips: (staffId, payslipIds, deliveryMethod) => axios.post(`${API_URL}/staff/${staffId}/payslips/batch-resend`, {
    payslip_ids: payslipIds,
    delivery_method: deliveryMethod
  }),
  
  // Holiday Bookings
  getHolidayBookings: (staffId) => axios.get(`${API_URL}/staff/${staffId}/holiday-bookings`),
  createHolidayBooking: (staffId, bookingData) => axios.post(`${API_URL}/staff/${staffId}/holiday-bookings`, bookingData),
  deleteHolidayBooking: (bookingId) => axios.delete(`${API_URL}/holiday-bookings/${bookingId}`),
  getHolidayBalance: (staffId) => axios.get(`${API_URL}/staff/${staffId}/holiday-balance`),
  
  // Area Manager Site Assignments
  getSiteAssignments: (staffId) => axios.get(`${API_URL}/staff/${staffId}/site-assignments`),
  updateSiteAssignments: (staffId, assignmentData) => axios.put(`${API_URL}/staff/${staffId}/site-assignments`, assignmentData),

  // Payroll Management
  getPayCycles: (includePast = false) => axios.get(`${API_URL}/pay-cycles?include_past=${includePast}`),
  getPayCycle: (cycleId) => axios.get(`${API_URL}/pay-cycles/${cycleId}`),
  generatePayCycles: () => axios.post(`${API_URL}/pay-cycles/generate`),
  updatePayCycleStatus: (cycleId, newStatus, force = false) => axios.put(`${API_URL}/pay-cycles/${cycleId}/status?new_status=${newStatus}&force=${force}`),
  calculatePayroll: (cycleId) => axios.post(`${API_URL}/pay-cycles/${cycleId}/calculate`),
  getPayrollEntries: (cycleId) => axios.get(`${API_URL}/pay-cycles/${cycleId}/entries`),
  getPayrollEntry: (entryId) => axios.get(`${API_URL}/payroll-entries/${entryId}`),
  updatePayrollEntry: (entryId, data) => axios.put(`${API_URL}/payroll-entries/${entryId}`, data),
  
  // NI Category Management
  checkNICategoryIssues: (cycleId) => axios.get(`${API_URL}/pay-cycles/${cycleId}/ni-category-check`),
  updateStaffNICategory: (staffId, newCategory) => axios.post(`${API_URL}/staff/${staffId}/update-ni-category?new_category=${newCategory}`),
  bulkUpdateNICategories: (updates) => axios.post(`${API_URL}/pay-cycles/bulk-update-ni-categories`, updates),

  // Timesheets
  generateTimesheets: (cycleId, regenerate = false) => axios.post(`${API_URL}/pay-cycles/${cycleId}/generate-timesheets${regenerate ? '?regenerate=true' : ''}`),
  deleteTimesheets: (cycleId) => axios.delete(`${API_URL}/pay-cycles/${cycleId}/timesheets`),
  getCycleTimesheets: (cycleId) => axios.get(`${API_URL}/pay-cycles/${cycleId}/timesheets`),
  getTimesheet: (timesheetId) => axios.get(`${API_URL}/timesheets/${timesheetId}`),
  updateTimesheet: (timesheetId, data) => axios.put(`${API_URL}/timesheets/${timesheetId}`, data),
  approveTimesheet: (timesheetId) => axios.put(`${API_URL}/timesheets/${timesheetId}/approve`),
  addCrossPayEntry: (timesheetId, data) => axios.post(`${API_URL}/timesheets/${timesheetId}/cross-pay`, data),

  // Payslips
  generatePayslips: (cycleId) => axios.post(`${API_URL}/pay-cycles/${cycleId}/generate-payslips`),
  getPayslips: (cycleId) => axios.get(`${API_URL}/pay-cycles/${cycleId}/payslips`),
  downloadPayslip: async (payslipId) => {
    const response = await axios.get(`${API_URL}/payslips/${payslipId}/download`, {
      responseType: 'blob'
    });
    return response;
  },
  
  // BACS Reports
  generateBACSReport: (cycleId, format) => axios.post(`${API_URL}/pay-cycles/${cycleId}/generate-bacs?format=${format}`, {}, {
    responseType: 'blob',  // Important for file download
  }),

  // HMRC RTI
  previewFPS: (cycleId) => axios.get(`${API_URL}/hmrc/fps/preview/${cycleId}`),
  validateFPS: (cycleId) => axios.get(`${API_URL}/hmrc/fps/validate/${cycleId}`),
  submitFPS: (cycleId) => axios.post(`${API_URL}/hmrc/fps/submit/${cycleId}`),
  previewEPS: (taxMonth) => axios.get(`${API_URL}/hmrc/eps/preview?tax_month=${taxMonth}`),
  submitEPS: (taxMonth) => axios.post(`${API_URL}/hmrc/eps/submit?tax_month=${taxMonth}`),
  getHMRCSubmissions: (type = null, status = null) => {
    let url = `${API_URL}/hmrc/submissions`;
    const params = [];
    if (type) params.push(`submission_type=${type}`);
    if (status) params.push(`status=${status}`);
    if (params.length) url += '?' + params.join('&');
    return axios.get(url);
  },
  getHMRCSubmission: (submissionId) => axios.get(`${API_URL}/hmrc/submissions/${submissionId}`),
  pollHMRCSubmission: (submissionId) => axios.post(`${API_URL}/hmrc/submissions/${submissionId}/poll`),
  
  // HMRC DPS (Data Provisioning Service)
  pollDPS: (autoApply = false) => axios.post(`${API_URL}/hmrc/dps/poll?auto_apply=${autoApply}`),
  getDPSNotifications: (status = null, type = null) => {
    let url = `${API_URL}/hmrc/dps/notifications`;
    const params = [];
    if (status) params.push(`status=${status}`);
    if (type) params.push(`notification_type=${type}`);
    if (params.length) url += '?' + params.join('&');
    return axios.get(url);
  },
  getDPSNotification: (notificationId) => axios.get(`${API_URL}/hmrc/dps/notifications/${notificationId}`),
  applyDPSNotification: (notificationId) => axios.post(`${API_URL}/hmrc/dps/notifications/${notificationId}/apply`),
  ignoreDPSNotification: (notificationId, reason = null) => 
    axios.post(`${API_URL}/hmrc/dps/notifications/${notificationId}/ignore${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  applyAllPendingDPS: (type = null) => 
    axios.post(`${API_URL}/hmrc/dps/apply-all-pending${type ? `?notification_type=${type}` : ''}`),

  // Company Pension Schemes
  getCompanyPensionSchemes: () => axios.get(`${API_URL}/pension-schemes`),
  createPensionScheme: (data) => axios.post(`${API_URL}/pension-schemes`, data),
  updatePensionScheme: (schemeId, data) => axios.put(`${API_URL}/pension-schemes/${schemeId}`, data),
  deletePensionScheme: (schemeId) => axios.delete(`${API_URL}/pension-schemes/${schemeId}`),

  // Tax Years
  getTaxYears: () => axios.get(`${API_URL}/payroll/tax-years`),
  getActiveTaxYear: () => axios.get(`${API_URL}/payroll/tax-years/active`),
  createTaxYear: (data) => axios.post(`${API_URL}/payroll/tax-years`, data),
  updateTaxYear: (taxYearId, data) => axios.put(`${API_URL}/payroll/tax-years/${taxYearId}`, data),
  deleteTaxYear: (taxYearId) => axios.delete(`${API_URL}/payroll/tax-years/${taxYearId}`),
  applyGlobalPayUpdate: (data) => axios.post(`${API_URL}/payroll/global-pay-update`, data),

  // Site Calendars
  getSiteCalendars: (siteId, taxYear) => axios.get(`${API_URL}/site-calendars`, { params: { site_id: siteId, tax_year: taxYear } }),
  createSiteCalendar: (data) => axios.post(`${API_URL}/site-calendars`, data),
  copySiteCalendar: (calendarId, newTaxYear) => axios.post(`${API_URL}/site-calendars/${calendarId}/copy?new_tax_year=${newTaxYear}`),
  updateSiteCalendar: (calendarId, data) => axios.put(`${API_URL}/site-calendars/${calendarId}`, data),

  // Intelligent Order Intake
  getPendingOrders: () => axios.get(`${API_URL}/pending-orders`),
  updatePendingOrder: (orderId, data) => axios.put(`${API_URL}/pending-orders/${orderId}`, data),
  approvePendingOrder: (orderId, routing) => axios.put(`${API_URL}/pending-orders/${orderId}/approve?routing=${routing}`),
  rejectPendingOrder: (orderId, reason) => axios.put(`${API_URL}/pending-orders/${orderId}/reject?reason=${reason}`),
  deletePendingOrder: (orderId) => axios.delete(`${API_URL}/pending-orders/${orderId}`),

  // Dashboard
  getDashboardStats: () => axios.get(`${API_URL}/dashboard/stats`),
  
  // Client Portal Users
  getClientUsers: (clientId) => axios.get(`${API_URL}/clients/${clientId}/users`),
  getAllPortalUsers: () => axios.get(`${API_URL}/portal-users`),
  createClientUser: (clientId, data) => axios.post(`${API_URL}/clients/${clientId}/users`, data),
  updateClientUser: (clientId, userId, data) => axios.put(`${API_URL}/clients/${clientId}/users/${userId}`, data),
  deleteClientUser: (clientId, userId) => axios.delete(`${API_URL}/clients/${clientId}/users/${userId}`),

  // Stocktake
  startStocktake: () => axios.post(`${API_URL}/stock/stocktake/start`),
  completeStocktake: (stocktakeId, updates) => axios.post(`${API_URL}/stock/stocktake/${stocktakeId}/complete`, updates),
  getStocktakes: () => axios.get(`${API_URL}/stock/stocktakes`),

  // P60 Endpoints
  listP60s: (taxYear) => axios.get(`${API_URL}/hmrc/p60/list`, { params: { tax_year: taxYear } }),
  getP60: (staffId, taxYear) => axios.get(`${API_URL}/hmrc/p60/${staffId}`, { params: { tax_year: taxYear }, responseType: 'blob' }),
  generateP60: (staffId, taxYear) => axios.post(`${API_URL}/hmrc/p60/generate/${staffId}`, null, { params: { tax_year: taxYear } }),

  // P32 Report
  getP32Report: (taxYear, format = 'json') => {
    const config = format === 'pdf' ? { responseType: 'blob' } : {};
    return axios.get(`${API_URL}/hmrc/p32`, { params: { tax_year: taxYear, format }, ...config });
  },

  // Year-End Processing
  checkYearEndReadiness: (taxYear) => axios.get(`${API_URL}/hmrc/year-end/check`, { params: { tax_year: taxYear } }),
  processYearEnd: (taxYear, options = {}) => axios.post(`${API_URL}/hmrc/year-end/process`, null, { 
    params: { 
      tax_year: taxYear,
      generate_p60s: options.generateP60s ?? true,
      submit_final_eps: options.submitFinalEps ?? true,
      test_mode: options.testMode ?? true
    } 
  }),
  getYearEndStatus: (taxYear) => axios.get(`${API_URL}/hmrc/year-end/status`, { params: { tax_year: taxYear } }),

  // Small Employer Relief & Employment Allowance
  getSERStatus: () => axios.get(`${API_URL}/hmrc/ser/status`),
  getEmploymentAllowanceStatus: () => axios.get(`${API_URL}/hmrc/employment-allowance/status`),
  enableEmploymentAllowance: (enabled) => axios.post(`${API_URL}/hmrc/employment-allowance/enable`, null, { params: { enabled } }),

  // Time & Attendance
  getTADevices: () => axios.get(`${API_URL}/ta-devices`),
  getTADevice: (id) => axios.get(`${API_URL}/ta-devices/${id}`),
  updateTADevice: (id, data) => axios.put(`${API_URL}/ta-devices/${id}`, null, { params: data }),
  deleteTADevice: (id) => axios.delete(`${API_URL}/ta-devices/${id}`),
  syncAllStaffToDevice: (deviceId) => axios.post(`${API_URL}/ta-devices/${deviceId}/sync-all-staff`),
  
  getTAStaffMappings: () => axios.get(`${API_URL}/ta-staff-mappings`),
  createTAStaffMapping: (data) => axios.post(`${API_URL}/ta-staff-mappings`, null, { params: data }),
  updateTAStaffMapping: (id, data) => axios.put(`${API_URL}/ta-staff-mappings/${id}`, null, { params: data }),
  syncStaffToDevice: (mappingId, deviceId) => axios.post(`${API_URL}/ta-staff-mappings/${mappingId}/sync-to-device/${deviceId}`),
  
  getAttendanceLogs: (params) => axios.get(`${API_URL}/attendance-logs`, { params }),
  getAttendanceLog: (id) => axios.get(`${API_URL}/attendance-logs/${id}`),
  getAttendanceSummary: (params) => axios.get(`${API_URL}/attendance-summary`, { params }),
  
  // Site Shifts
  getSiteShifts: (siteId) => axios.get(`${API_URL}/site-shifts`, { params: siteId ? { site_id: siteId } : {} }),
  getSiteShift: (id) => axios.get(`${API_URL}/site-shifts/${id}`),
  createSiteShift: (data) => axios.post(`${API_URL}/site-shifts`, null, { params: data }),
  updateSiteShift: (id, data) => axios.put(`${API_URL}/site-shifts/${id}`, null, { params: data }),
  deleteSiteShift: (id) => axios.delete(`${API_URL}/site-shifts/${id}`),
  
  // System Settings
  getSystemSettings: () => axios.get(`${API_URL}/system-settings`),
  getMissingClockouts: (date) => axios.get(`${API_URL}/attendance/missing-clockouts`, { params: date ? { date } : {} }),
  updateSystemSettings: (data) => axios.put(`${API_URL}/system-settings`, null, { params: data }),
  
  // Attendance Alerts
  getAttendanceAlerts: (params) => axios.get(`${API_URL}/attendance-alerts`, { params }),
  checkAttendanceAlertsNow: (siteId) => axios.post(`${API_URL}/attendance-alerts/check-now`, null, { params: siteId ? { site_id: siteId } : {} }),

  // HR Portal - Cases
  getHRCases: (params) => axios.get(`${API_URL}/hr/cases`, { params }),
  getHRCaseStats: () => axios.get(`${API_URL}/hr/cases/stats`),
  getHRCase: (id) => axios.get(`${API_URL}/hr/cases/${id}`),
  createHRCase: (data) => axios.post(`${API_URL}/hr/cases`, data),
  updateHRCase: (id, data) => axios.put(`${API_URL}/hr/cases/${id}`, data),
  addHRCaseNote: (caseId, content, noteType = 'note', isInternal = true) => 
    axios.post(`${API_URL}/hr/cases/${caseId}/notes`, null, { 
      params: { content, note_type: noteType, is_internal: isInternal } 
    }),
  
  // HR Portal - PIPs
  getPIPs: (params) => axios.get(`${API_URL}/hr/pips`, { params }),
  getPIP: (id) => axios.get(`${API_URL}/hr/pips/${id}`),
  createPIP: (data) => axios.post(`${API_URL}/hr/pips`, data),
  updatePIP: (id, data) => axios.put(`${API_URL}/hr/pips/${id}`, data),
  addPIPReview: (pipId, reviewNotes, progressRating = 'on_track') => 
    axios.post(`${API_URL}/hr/pips/${pipId}/reviews`, null, { 
      params: { review_notes: reviewNotes, progress_rating: progressRating } 
    }),
  
  // HR Portal - Staff History
  getStaffHRHistory: (staffId) => axios.get(`${API_URL}/hr/staff/${staffId}/history`),

  // HR Portal - Bradford Alerts (Phase 4)
  getHRBradfordAlerts: () => axios.get(`${API_URL}/hr/bradford-alerts`),
  createHRCaseFromBradford: (staffId) => axios.post(`${API_URL}/hr/bradford-alerts/${staffId}/create-case`),

  // HR Portal - Reports (Phase 5)
  getHRReportSummary: (startDate, endDate) => axios.get(`${API_URL}/hr/reports/summary`, { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  getHRWorkloadReport: () => axios.get(`${API_URL}/hr/reports/workload`),
  checkHRSLAWarnings: () => axios.post(`${API_URL}/hr/check-sla-warnings`),
};